// Live counter for a column with a `min_length`, drawn INSIDE the field.
//
// A small progress ring fills as the user types; once the minimum is met the
// ring becomes a check and the count turns green. It sits in the field's
// trailing corner (`placement="inline"` for single-line inputs, `"corner"` for
// multi-line editors), so the wrapped control reserves room for it via the
// padding returned by `minLengthPadding`. Advisory while typing — the engine
// enforces the same rule on save (see txn/textLength.ts).

import type { CSSProperties, ReactNode } from 'react'
import { Check } from 'lucide-react'

export type MinLengthPlacement = 'inline' | 'corner'

const OK = '#16a34a'

/** Right padding a wrapped single-line input needs so text never runs under the counter. */
export function minLengthPadding(min: number): number {
  return 34 + String(min).length * 14
}

function Ring({ ratio }: { ratio: number }) {
  const r = 5.5
  const c = 2 * Math.PI * r
  return (
    <svg width={14} height={14} viewBox="0 0 14 14" aria-hidden style={{ flexShrink: 0 }}>
      <circle cx={7} cy={7} r={r} fill="none" stroke="var(--color-border)" strokeWidth={2} />
      <circle
        cx={7} cy={7} r={r} fill="none" stroke="var(--color-accent)" strokeWidth={2}
        strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - ratio)}
        transform="rotate(-90 7 7)" style={{ transition: 'stroke-dashoffset 0.2s ease' }}
      />
    </svg>
  )
}

export function MinLengthBadge({ count, min, placement = 'inline' }: {
  count: number
  min: number
  placement?: MinLengthPlacement
}) {
  const met = count >= min
  const pos: CSSProperties = placement === 'inline'
    ? { top: '50%', right: 8, transform: 'translateY(-50%)' }
    : { bottom: 6, right: 8 }
  return (
    <span
      role="status"
      aria-live="polite"
      title={met ? `Minimum of ${min} characters reached` : `At least ${min} characters — ${count} so far`}
      style={{
        position: 'absolute', ...pos, zIndex: 1, pointerEvents: 'none',
        display: 'inline-flex', alignItems: 'center', gap: 4,
        padding: '1px 6px 1px 4px', borderRadius: 999,
        fontSize: '0.8125rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums',
        lineHeight: '16px', whiteSpace: 'nowrap',
        color: met ? OK : 'var(--color-text-secondary)',
        background: met ? 'rgba(22,163,74,0.10)' : 'var(--color-bg-secondary)',
        transition: 'color 0.2s, background 0.2s',
      }}
    >
      {met
        ? <Check size={12} strokeWidth={3} aria-hidden />
        : <Ring ratio={min > 0 ? Math.min(1, count / min) : 1} />}
      {met ? count : `${count}/${min}`}
    </span>
  )
}

/** Wraps a control so the badge can sit inside it. Renders the control as-is when `min` is null. */
export function WithMinLength({ min, count, placement, show = true, style, children }: {
  min: number | null
  count: number
  placement: MinLengthPlacement
  show?: boolean
  style?: CSSProperties
  children: ReactNode
}) {
  if (min == null || !show) return <>{children}</>
  return (
    <div style={{ position: 'relative', display: 'flex', flexDirection: 'column', ...style }}>
      {children}
      <MinLengthBadge count={count} min={min} placement={placement} />
    </div>
  )
}
